import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Shield, AlertTriangle, Clock, CheckCircle, Calendar, RefreshCw, Plus, Download, FileText, Loader2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { fyDropdownOptions } from '../lib/fyUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ComplianceDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [dashboard, setDashboard] = useState(null);
  const [complianceItems, setComplianceItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ compliance_type: 'pf_return', period: new Date().toISOString().slice(0, 7), due_date: '', description: '', state: '' });
  const [pdfPeriod, setPdfPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [pdfDownloading, setPdfDownloading] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  // Stage 73.14 — FY + Quarter selectors for Form 12BB and Form 24Q.
  // Default to the current Indian financial year (Apr→Mar).
  const _now = new Date();
  const _fyStart = _now.getMonth() >= 3 ? _now.getFullYear() : _now.getFullYear() - 1;
  const [fy, setFy] = useState(`${_fyStart}-${String(_fyStart + 1).slice(2)}`);
  // Default quarter from current month.
  const [quarter, setQuarter] = useState(() => {
    const m = _now.getMonth() + 1;
    if (m >= 4 && m <= 6) return 'Q1';
    if (m >= 7 && m <= 9) return 'Q2';
    if (m >= 10 && m <= 12) return 'Q3';
    return 'Q4';
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchCompliance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/statutory/compliance?company_id=${selectedCompany}`, { withCredentials: true });
      setComplianceItems(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/statutory/dashboard?company_id=${selectedCompany}`, { withCredentials: true });
      setDashboard(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [selectedCompany]);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data || []);
      if (data?.length > 0 && !selectedEmployee) setSelectedEmployee(data[0].id);
    } catch (e) { console.error(e); }
  }, [selectedCompany, selectedEmployee]);

  useEffect(() => { if (selectedCompany) { fetchDashboard(); fetchCompliance(); fetchEmployees(); } }, [selectedCompany, fetchCompliance, fetchDashboard, fetchEmployees]);





  const handleDownloadPdf = async (reportType) => {
    setPdfDownloading(reportType);
    try {
      let url = '';
      let filename = '';
      if (reportType === 'form16') {
        if (!selectedEmployee) { toast.error('Select an employee for Form 16'); setPdfDownloading(null); return; }
        // Stage 73.20k — use the user-selected FY (was hardcoded 2025-26).
        url = `${API_URL}/reports/form16/pdf?company_id=${selectedCompany}&employee_id=${selectedEmployee}&financial_year=${fy}`;
        filename = `Form16_${fy}.pdf`;
      } else if (reportType === 'form16_bulk') {
        // Bulk Form 16 ZIP — backend loops every active employee in the company
        // and stitches the PDFs into a single archive (any errors land in _errors.txt).
        url = `${API_URL}/reports/form16/bulk-zip?company_id=${selectedCompany}&financial_year=${fy}`;
        filename = `Form16_Bulk_${fy}.zip`;
      } else if (reportType === 'pf') {
        url = `${API_URL}/reports/pf-register/pdf?company_id=${selectedCompany}&period=${pdfPeriod}`;
        filename = `PF_Register_${pdfPeriod}.pdf`;
      } else if (reportType === 'esi') {
        url = `${API_URL}/reports/esi-register/pdf?company_id=${selectedCompany}&period=${pdfPeriod}`;
        filename = `ESI_Register_${pdfPeriod}.pdf`;
      } else if (reportType === 'pt') {
        url = `${API_URL}/reports/pt-register/pdf?company_id=${selectedCompany}&period=${pdfPeriod}&state=Maharashtra`;
        filename = `PT_Register_${pdfPeriod}.pdf`;
      } else if (reportType === 'form12bb') {
        if (!selectedEmployee) { toast.error('Select an employee for Form 12BB'); setPdfDownloading(null); return; }
        url = `${API_URL}/reports/form-12bb/pdf?company_id=${selectedCompany}&employee_id=${selectedEmployee}&financial_year=${fy}`;
        filename = `Form_12BB_${fy}.pdf`;
      } else if (reportType === 'form24q') {
        url = `${API_URL}/reports/form-24q/pdf?company_id=${selectedCompany}&quarter=${quarter}&financial_year=${fy}`;
        filename = `Form_24Q_${quarter}_${fy}.pdf`;
      }
      const response = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${reportType === 'form16_bulk' ? 'Bulk Form 16 ZIP' : reportType.toUpperCase() + ' report'} downloaded`);
    } catch (err) {
      // Blob errors don't carry parseable JSON; tell the user to retry.
      const msg = err.response?.data && err.response.data instanceof Blob
        ? 'Failed to download report — check that payroll runs exist for this period.'
        : formatApiError(err);
      toast.error(msg);
    } finally {
      setPdfDownloading(null);
    }
  };

  const handleAddCompliance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/statutory/compliance`, { ...form, company_id: selectedCompany }, { withCredentials: true });
      toast.success('Compliance item added');
      setShowAddModal(false);
      fetchCompliance(); fetchDashboard();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/statutory/compliance/${id}`, { status }, { withCredentials: true });
      fetchCompliance(); fetchDashboard();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const COMPLIANCE_TYPES = [
    { value: 'pf_return', label: 'PF Return' }, { value: 'pf_challan', label: 'PF Challan' },
    { value: 'esi_return', label: 'ESI Return' }, { value: 'esi_challan', label: 'ESI Challan' },
    { value: 'pt_return', label: 'PT Return' }, { value: 'tds_return', label: 'TDS Return (24Q)' },
    { value: 'tds_challan', label: 'TDS Challan' }, { value: 'lwf_return', label: 'LWF Return' },
    { value: 'form_16', label: 'Form 16 Issuance' }, { value: 'form_12bb', label: 'Form 12BB Collection' },
    { value: 'bonus_payment', label: 'Bonus Payment' }, { value: 'gratuity', label: 'Gratuity Payment' },
  ];

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (status === 'in_progress') return <Clock className="w-4 h-4 text-amber-500" />;
    return <AlertTriangle className="w-4 h-4 text-red-500" />;
  };

  return (
    <MainLayout>
      <div data-testid="compliance-dashboard-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compliance Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Track statutory due dates, filing status, and compliance health</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button onClick={() => { fetchDashboard(); fetchCompliance(); }} className="flex items-center space-x-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm hover:bg-slate-50"><RefreshCw className="w-4 h-4" /><span>Refresh</span></button>
            <button data-testid="add-compliance-btn" onClick={() => setShowAddModal(true)} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]"><Plus className="w-4 h-4" /><span>Add Item</span></button>
          </div>
        </div>

        {dashboard && (
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-red-50 rounded-xl border border-red-200 p-4 text-center">
              <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-700">{dashboard.overdue_count}</p>
              <p className="text-xs text-red-500">Overdue</p>
            </div>
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-700">{dashboard.due_soon_count}</p>
              <p className="text-xs text-amber-500">Due Soon</p>
            </div>
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
              <Shield className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{dashboard.param_count}</p>
              <p className="text-xs text-blue-500">Active Rules</p>
            </div>
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 text-center">
              <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-700">{dashboard.employee_statutory_count}</p>
              <p className="text-xs text-emerald-500">Employees Enrolled</p>
            </div>
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-4 text-center">
              <Calendar className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{dashboard.declarations_pending}</p>
              <p className="text-xs text-purple-500">Declarations Pending</p>
            </div>
          </div>
        )}

        {dashboard?.overdue_items?.length > 0 && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-4">
            <h3 className="text-sm font-semibold text-red-800 mb-3 flex items-center space-x-2"><AlertTriangle className="w-4 h-4" /><span>Overdue Items</span></h3>
            <div className="space-y-2">
              {dashboard.overdue_items.map((item) => (
                <div key={item.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-100">
                  <div><p className="text-sm font-medium text-slate-800">{item.compliance_type?.replace('_', ' ').toUpperCase()}</p><p className="text-xs text-red-500">Due: {item.due_date} | Period: {item.period}</p></div>
                  <button onClick={() => handleStatusChange(item.id, 'completed')} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700">Mark Done</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {dashboard?.recent_challans?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Recent Challans</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dashboard.recent_challans.map((c) => (
                <div key={c.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className="flex items-center justify-between mb-1"><span className="text-xs font-medium text-slate-600">{c.challan_type?.toUpperCase()}</span><span className={`text-xs px-1.5 py-0.5 rounded ${c.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{c.status}</span></div>
                  <p className="text-sm font-bold text-slate-900">{(c.grand_total || c.total_pt || c.total_tds || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-400">{c.period}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statutory PDF Reports */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="statutory-pdf-reports-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#0052FF]" /> Statutory PDF Reports
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Download compliance registers and certificates as PDF</p>
            </div>
            <input type="month" value={pdfPeriod} onChange={e => setPdfPeriod(e.target.value)}
              data-testid="pdf-period-selector"
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Form 16 */}
            <div className="border border-slate-200 rounded-lg p-4 hover:border-[#0052FF]/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><FileText className="w-4 h-4 text-blue-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Form 16</p>
                  <p className="text-xs text-slate-500">TDS Certificate</p>
                </div>
              </div>
              <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}
                data-testid="form16-employee-select"
                className="w-full border border-slate-200 rounded-md px-2 py-1 text-xs mb-2">
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{`${emp.personal_details?.first_name} ${emp.personal_details?.last_name} (${emp.employment_details?.employee_code})`}</option>
                ))}
              </select>
              <button data-testid="download-form16-btn" onClick={() => handleDownloadPdf('form16')} disabled={pdfDownloading === 'form16'}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'form16' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download Form 16
              </button>
              <button data-testid="download-form16-bulk-btn" onClick={() => handleDownloadPdf('form16_bulk')} disabled={pdfDownloading === 'form16_bulk'}
                title="Generates Form 16 PDFs for every active employee in this company and downloads them as a single ZIP."
                className="w-full mt-1.5 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200 rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'form16_bulk' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />} Bulk Download (ZIP)
              </button>
            </div>

            {/* PF Register */}
            <div className="border border-slate-200 rounded-lg p-4 hover:border-[#0052FF]/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><FileText className="w-4 h-4 text-emerald-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">PF Register</p>
                  <p className="text-xs text-slate-500">EPF Contribution</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-2">Period: {pdfPeriod}</p>
              <button data-testid="download-pf-register-btn" onClick={() => handleDownloadPdf('pf')} disabled={pdfDownloading === 'pf'}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'pf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download PF Register
              </button>
            </div>

            {/* ESI Register */}
            <div className="border border-slate-200 rounded-lg p-4 hover:border-[#0052FF]/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><FileText className="w-4 h-4 text-amber-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">ESI Register</p>
                  <p className="text-xs text-slate-500">ESIC Contribution</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-2">Period: {pdfPeriod}</p>
              <button data-testid="download-esi-register-btn" onClick={() => handleDownloadPdf('esi')} disabled={pdfDownloading === 'esi'}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'esi' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download ESI Register
              </button>
            </div>

            {/* PT Register */}
            <div className="border border-slate-200 rounded-lg p-4 hover:border-[#0052FF]/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center"><FileText className="w-4 h-4 text-purple-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">PT Register</p>
                  <p className="text-xs text-slate-500">Professional Tax</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-2">Period: {pdfPeriod}</p>
              <button data-testid="download-pt-register-btn" onClick={() => handleDownloadPdf('pt')} disabled={pdfDownloading === 'pt'}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'pt' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download PT Register
              </button>
            </div>

            {/* Stage 73.14 — Form 12BB (employee tax declaration) */}
            <div className="border border-slate-200 rounded-lg p-4 hover:border-[#0052FF]/40 transition-colors" data-testid="form-12bb-card">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center"><FileText className="w-4 h-4 text-indigo-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Form 12BB</p>
                  <p className="text-xs text-slate-500">Employee Tax Declaration</p>
                </div>
              </div>
              <div className="flex gap-2 mb-2">
                <select value={fy} onChange={e => setFy(e.target.value)} data-testid="form12bb-fy-select"
                  className="border border-slate-200 rounded-md px-2 py-1 text-xs flex-1">
                  {fyDropdownOptions(2, 2).map(label => (
                    <option key={label} value={label}>{`FY ${label}`}</option>
                  ))}
                </select>
              </div>
              <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}
                data-testid="form12bb-employee-select"
                className="w-full border border-slate-200 rounded-md px-2 py-1 text-xs mb-2">
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{`${emp.personal_details?.first_name} ${emp.personal_details?.last_name} (${emp.employment_details?.employee_code})`}</option>
                ))}
              </select>
              <button data-testid="download-form12bb-btn" onClick={() => handleDownloadPdf('form12bb')} disabled={pdfDownloading === 'form12bb'}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'form12bb' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download Form 12BB
              </button>
            </div>

            {/* Stage 73.14 — Form 24Q (quarterly TDS return, Annexure I) */}
            <div className="border border-slate-200 rounded-lg p-4 hover:border-[#0052FF]/40 transition-colors" data-testid="form-24q-card">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center"><FileText className="w-4 h-4 text-rose-600" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Form 24Q</p>
                  <p className="text-xs text-slate-500">Quarterly TDS Return (Annexure I)</p>
                </div>
              </div>
              <div className="flex gap-2 mb-2">
                <select value={fy} onChange={e => setFy(e.target.value)} data-testid="form24q-fy-select"
                  className="border border-slate-200 rounded-md px-2 py-1 text-xs flex-1">
                  {fyDropdownOptions(2, 2).map(label => (
                    <option key={label} value={label}>{`FY ${label}`}</option>
                  ))}
                </select>
                <select value={quarter} onChange={e => setQuarter(e.target.value)} data-testid="form24q-quarter-select"
                  className="border border-slate-200 rounded-md px-2 py-1 text-xs">
                  <option value="Q1">Q1 · Apr-Jun</option>
                  <option value="Q2">Q2 · Jul-Sep</option>
                  <option value="Q3">Q3 · Oct-Dec</option>
                  <option value="Q4">Q4 · Jan-Mar</option>
                </select>
              </div>
              <button data-testid="download-form24q-btn" onClick={() => handleDownloadPdf('form24q')} disabled={pdfDownloading === 'form24q'}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                {pdfDownloading === 'form24q' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download Form 24Q
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100"><h2 className="text-base font-semibold text-slate-800">Compliance Tracker</h2></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="px-4 py-3 text-left font-medium">Compliance Type</th>
              <th className="px-4 py-3 text-left font-medium">Period</th>
              <th className="px-4 py-3 text-left font-medium">Due Date</th>
              <th className="px-4 py-3 text-left font-medium">State</th>
              <th className="px-4 py-3 text-center font-medium">Status</th>
              <th className="px-4 py-3 text-center font-medium">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {complianceItems.map((item) => (
                <tr key={item.id} data-testid={`compliance-row-${item.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 flex items-center space-x-2">{statusIcon(item.status)}<span className="text-slate-800 font-medium">{item.compliance_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span></td>
                  <td className="px-4 py-3 text-slate-600">{item.period}</td>
                  <td className="px-4 py-3 text-slate-600">{item.due_date}</td>
                  <td className="px-4 py-3 text-slate-600">{item.state || 'All'}</td>
                  <td className="px-4 py-3 text-center"><span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : item.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>{item.status}</span></td>
                  <td className="px-4 py-3 text-center">
                    <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value)} className="text-xs border border-slate-300 rounded px-2 py-1">
                      <option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
                    </select>
                  </td>
                </tr>
              ))}
              {complianceItems.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No compliance items. Click "Add Item" to track due dates.</td></tr>}
            </tbody>
          </table>
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Compliance Item</h3>
              <form onSubmit={handleAddCompliance} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Compliance Type</label>
                  <select data-testid="compliance-type-select" value={form.compliance_type} onChange={(e) => setForm({ ...form, compliance_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    {COMPLIANCE_TYPES.map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Period</label><input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label><input type="date" data-testid="compliance-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">State (optional)</label><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="e.g. Maharashtra" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-compliance-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ComplianceDashboard;
